import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ROLE_DISPLAY_NAMES } from '@ub/shared';
import { inviteApi } from '../services/api';
import { useAuthStore } from '../stores/auth-store';

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const nav = useNavigate();
  const hydrate = useAuthStore((s) => s.hydrate);

  const infoQ = useQuery({
    queryKey: ['invite-info', token],
    queryFn: () => inviteApi.info(token),
    enabled: !!token,
    retry: false,
  });

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (infoQ.data?.name) setName(infoQ.data.name);
  }, [infoQ.data]);

  const acceptM = useMutation({
    mutationFn: () => inviteApi.accept({ token, name, password }),
    onSuccess: async () => {
      await hydrate();
      nav('/projects', { replace: true });
    },
    onError: (e: unknown) => {
      const err = e as { code?: string; message?: string };
      // Validation errors carry the issue list in message — show that directly.
      if (err.code === 'validation_error' && err.message) {
        setError(err.message);
      } else {
        setError(humanize(err.code ?? 'error'));
      }
    },
  });

  const passwordRules = [
    { test: password.length >= 8, label: 'At least 8 characters' },
  ];
  const passwordOk = passwordRules.every((r) => r.test);

  if (!token) {
    return <Centered>
      <h1 className="text-xl font-bold text-brand-navy mb-2">Missing token</h1>
      <p className="text-sm text-slate-500">No invitation token was provided.</p>
    </Centered>;
  }
  if (infoQ.isLoading) return <Centered><p className="text-slate-500">Loading invitation…</p></Centered>;
  if (infoQ.isError || !infoQ.data) {
    const code = (infoQ.error as { code?: string } | null)?.code ?? 'invalid_token';
    return <Centered>
      <h1 className="text-xl font-bold text-brand-navy mb-2">Invitation unavailable</h1>
      <p className="text-sm text-slate-500">{humanize(code)}</p>
      <p className="text-xs text-slate-400 mt-4">Ask your admin to send a fresh invite.</p>
    </Centered>;
  }

  const inv = infoQ.data;

  return (
    <Centered>
      <h1 className="text-xl font-bold text-brand-navy mb-1">Welcome to {inv.companyName}</h1>
      <p className="text-sm text-slate-600 mb-1">
        {inv.inviterName ?? 'An admin'} invited you as <strong>{ROLE_DISPLAY_NAMES[inv.role]}</strong>.
      </p>
      <p className="text-xs text-slate-500 mb-6">Set up your account and you'll be signed in.</p>

      <form onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!passwordOk) { setError('Password does not meet the requirements below.'); return; }
        if (password !== confirm) { setError('Passwords do not match.'); return; }
        acceptM.mutate();
      }} className="space-y-3">
        <Field label="Email">
          <input value={inv.email} disabled autoComplete="username"
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-100 text-slate-500" />
        </Field>
        <Field label="Your name">
          <input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
        </Field>
        <Field label="Password">
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            autoComplete="new-password" />
          {password.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs">
              {passwordRules.map((r) => (
                <li key={r.label} className={r.test ? 'text-emerald-700' : 'text-slate-500'}>
                  {r.test ? '✓' : '○'} {r.label}
                </li>
              ))}
            </ul>
          )}
        </Field>
        <Field label="Confirm password">
          <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            autoComplete="new-password" />
        </Field>
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2 break-words">{error}</div>}
        <button type="submit" disabled={acceptM.isPending}
          className="w-full bg-brand-navy text-white font-semibold rounded-md py-2 hover:bg-brand-navy/90 disabled:opacity-50">
          {acceptM.isPending ? 'Activating…' : 'Activate account'}
        </button>
      </form>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-slate-200 p-8">
        {children}
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function humanize(code: string): string {
  if (code === 'invalid_token') return 'This invitation link is invalid.';
  if (code === 'invite_revoked') return 'This invitation has been revoked by an admin.';
  if (code === 'invite_already_accepted') return 'This invitation has already been used. Sign in with your password instead.';
  if (code === 'invite_expired') return 'This invitation has expired. Ask your admin to re-send it.';
  if (code === 'conflict') return 'A user with that email already exists. Sign in instead.';
  return `Error: ${code}`;
}
